'use client'

import { TabsContactForm } from '@/components/Forms/TabsContactForm.client'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'
import Image from 'next/image'
import React, { useEffect, useMemo, useRef, useState } from 'react'

import { NavArrowIcon } from '@/components/icons/NavArrowIcon'
import RichText from '@/components/RichText'
import { outfit } from '@/fonts'
import { cn } from '@/utilities/ui'
import PlusCircleIcon from '@/components/icons/PlusCircleIcon'
import { HeadingWithBreaks } from '@/components/HeadingWithBreaks'
import {
  FloorplanSectionClient,
  type FloorplanPlanData,
} from '@/blocks/FloorplanSection/Component.client'

/** Design tokens */
const ACCENT_BAR = '#4BA3B0'

export type HomepageTabbedSectionTabPayload = {
  id: string
  label: string
  useContactForm: boolean
  contactForm: unknown | null
  useFloorplan: boolean
  plans: FloorplanPlanData[]
  heading: string
  subHeading: string | null
  body: DefaultTypedEditorState | null
  imageUrl: string | null
  imageAlt: string | null
  image2Url: string | null
  image2Alt: string | null
  logoUrl: string | null
  logoAlt: string | null
}

type Props = {
  tabs: HomepageTabbedSectionTabPayload[]
  defaultActiveIndex: number
  className?: string
}

export const HomepageTabbedSectionClient: React.FC<Props> = ({
  tabs,
  defaultActiveIndex,
  className,
}) => {
  const safeDefault = useMemo(() => {
    if (tabs.length === 0) return 0
    const i = Number.isFinite(defaultActiveIndex) ? Math.floor(defaultActiveIndex) : 2
    return Math.min(Math.max(0, i), tabs.length - 1)
  }, [defaultActiveIndex, tabs.length])

  const [active, setActive] = useState(safeDefault)
  const tabBtnRefs = useRef<(HTMLButtonElement | null)[]>([])
  const userInteracted = useRef(false)

  /** On mobile, keep the selected tab in view — only after a user interaction. */
  useEffect(() => {
    if (!userInteracted.current) return
    const el = tabBtnRefs.current[active]
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }, [active])

  if (tabs.length === 0) return null

  const current = tabs[active] ?? tabs[0]

  const hasTabImage1 = Boolean(current.imageUrl)
  const hasTabImage2 = Boolean(current.image2Url)
  /** Exactly one of the two slots filled — use combined height of two 4:3 frames (ratio 2:3). */
  const tabSingleImageFullColumn = hasTabImage1 !== hasTabImage2

  return (
    <section
      className={cn('w-full bg-white px-[10px] pt-[40px] font-sans md:pt-10 md:pb-5', className)}
    >
      <div className="mx-auto max-w-[1140px] px-0">
        <nav
          aria-label="Section tabs"
          className={cn(
            'scrollbar-hide mb-[11px] flex flex-nowrap justify-start gap-3 overflow-x-auto overflow-y-hidden overscroll-x-contain px-4 pb-2 snap-x snap-mandatory [-webkit-overflow-scrolling:touch] sm:px-5 md:mb-10 md:gap-[60px] lg:px-6',
            'min-[1140px]:flex min-[1140px]:flex-nowrap min-[1140px]:gap-6 min-[1140px]:overflow-visible min-[1140px]:snap-none min-[1140px]:px-0 min-[1140px]:pb-0',
          )}
          role="tablist"
        >
          {tabs.map((tab, index) => {
            const isActive = index === active
            const showArrow = tab.useContactForm
            return (
              <button
                key={tab.id}
                ref={(node) => {
                  tabBtnRefs.current[index] = node
                }}
                type="button"
                aria-controls={`tabpanel-${tab.id}`}
                aria-selected={isActive}
                className={cn(
                  'group inline-flex shrink-0 snap-start items-center font-bold gap-2 whitespace-nowrap px-[35px] py-[15px] text-sm transition-colors hover:pointer-cursor md:text-[15px]',
                  'min-[1140px]:flex-1 min-[1140px]:min-w-auto min-[1140px]:justify-center',
                  isActive
                    ? 'cursor-default text-white bg-[#8DC9D1]'
                    : 'cursor-pointer text-[#4f5555] bg-[#F7E8B1] hover:text-[#009BAB] hover:bg-[#96969647]',
                )}
                id={`tab-${tab.id}`}
                role="tab"
                tabIndex={isActive ? 0 : -1}
                onClick={() => {
                  userInteracted.current = true
                  setActive(index)
                }}
              >
                <span>{tab.label}</span>
                <span
                  className="inline-flex h-5 w-5 shrink-0 items-center justify-center"
                  aria-hidden
                >
                  {showArrow ? (
                    <NavArrowIcon
                      className={cn(
                        'h-6 w-6 shrink-0',
                        isActive ? 'text-[#349BAA]' : 'text-black group-hover:text-[#009BAB]',
                      )}
                      size={14}
                    />
                  ) : (
                    <PlusCircleIcon
                      className={cn(
                        'h-4 w-4 shrink-0 transition-colors',
                        isActive ? 'text-[#349BAA]' : 'text-black group-hover:text-[#009BAB]',
                      )}
                      size={14}
                    />
                  )}
                </span>
              </button>
            )
          })}
        </nav>

        <div
          aria-labelledby={`tab-${current.id}`}
          className="flex"
          id={`tabpanel-${current.id}`}
          role="tabpanel"
        >
          {/* Teal accent bar — hidden when contact tab is active */}
          {!current.useContactForm && (
            <div className="hidden lg:block w-[30px] shrink-0 bg-[#009BAB]" aria-hidden />
          )}

          {/* Content with responsive padding */}
          <div className="flex-1   lg:pl-[10px] 2xl:pl-[30px] grid grid-cols-1 items-center gap-[20px] md:grid-cols-2 md:items-stretch ">
            {current.useFloorplan ? (
              <div className="md:col-span-2">
                <FloorplanSectionClient plans={current.plans} />
              </div>
            ) : current.useContactForm && current.contactForm ? (
              <div className="flex flex-col md:col-span-2 ">
                {current.logoUrl || current.heading?.trim() || current.subHeading?.trim() ? (
                  <div className="flex flex-col items-center text-center">
                    {current.logoUrl ? (
                      <div className="relative mx-auto mb-3 h-[62px] w-[min(100%,200px)] max-w-full">
                        <Image
                          alt={current.logoAlt || ''}
                          className="object-contain object-center w-[120px]"
                          fill
                          sizes="200px"
                          src={current.logoUrl}
                        />
                      </div>
                    ) : null}
                    {current.heading?.trim() ? (
                      <div className="flex w-full max-w-4xl items-center justify-center gap-3  sm:gap-4">
                        <div
                          className="hidden sm:block h-px min-w-6 flex-1 max-w-[104px] shrink"
                          style={{ backgroundColor: ACCENT_BAR }}
                          aria-hidden
                        />
                        <h2
                          className={cn(
                            outfit.className,
                            'text-[24px] leading-[24px] font-semibold tracking-[1px] text-black whitespace-nowrap',
                          )}
                        >
                          {current.heading.trim()}
                        </h2>
                        <div
                          className="hidden sm:block h-px min-w-6 flex-1 max-w-[104px] shrink"
                          style={{ backgroundColor: ACCENT_BAR }}
                          aria-hidden
                        />
                      </div>
                    ) : null}
                    {current.subHeading?.trim() ? (
                      <p
                        className={cn(
                          outfit.className,
                          'mt-4 text-[16px] leading-relaxed text-[#1d1d1b] border-b border-[#009BAB] pb-3 font-thin w-full',
                        )}
                      >
                        {current.subHeading.trim()}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                <TabsContactForm
                  source="homepage-tabs-form"
                  showSpacer={true}
                />
              </div>
            ) : (
              <>
                <div className="min-w-0 overflow-hidden ">
                  <div className="flex gap-2.5 lg:gap-6">
                    <div className="lg:hidden w-[30px] shrink-0 bg-[#009BAB]" aria-hidden />
                    <div className="min-w-0 flex-1 pt-[10px] pr-[30px] pb-[10px]">
                      <HeadingWithBreaks
                        text={current.heading}
                        className={cn(
                          outfit.className,
                          'font-medium tracking-tight md:p-[10px] text-[rgb(29,29,27)] break-words',
                        )}
                        sizes={[
                          'text-[54px]',
                          'md:text-[72px]',
                          'lg:text-[72px]',
                          'xl:text-[76px]',
                        ]}
                        leadings={[
                          'leading-[1.2em]',
                          'md:leading-[1.2em]',
                          'lg:leading-[1.2em]',
                          'xl:leading-[1.2em]',
                        ]}
                      />
                      {current.subHeading?.trim() ? (
                        <p
                          className={cn(
                            outfit.className,
                            'mt-4 block w-full border border-[#4BA3B0] p-[10px] text-[30px] font-thin tracking-[0.23em] text-[#4BA3B0] lg:mt-5 md:text-[40px] leading-none',
                          )}
                        >
                          {current.subHeading.trim() === 'Building Legacies'
                            ? current.subHeading
                                .trim()
                                .split(' ')
                                .map((word, i) => (
                                  <span key={i} className="inline md:block">
                                    {word}{' '}
                                  </span>
                                ))
                            : current.subHeading.trim()}
                        </p>
                      ) : null}
                      {current.body ? (
                        <div className="mt-6 max-w-prose text-[#1D1D1B] lg:mt-8">
                          <RichText
                            className="[&_a]:text-[#4BA3B0] [&_a]:underline [&_li]:text-[17px] [&_li]:leading-relaxed [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:text-[17px] [&_p]:mb-4 [&_p]:text-[17px] [&_p]:leading-relaxed [&_p:last-child]:mb-0 [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:text-[17px]"
                            data={current.body}
                            enableGutter={false}
                            enableProse={false}
                          />
                        </div>
                      ) : null}
                      {current.logoUrl ? (
                        <div className="relative mx-auto  max-w-full flex flex-col items-center justify-center mt-4 space-y-2">
                          <div className="relative w-[135px] h-[62px] max-h-[62px] mb-0">
                            <Image
                              alt={current.logoAlt || ''}
                              className="object-contain object-left"
                              fill
                              sizes="160px"
                              src={current.logoUrl}
                            />
                          </div>
                          <p className="text-[12px] font-thin text-[#1d1d1b]">
                            San Diego&apos;s Most Trusted Contractor
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div
                  key={current.id}
                  className={cn(
                    'relative flex w-full flex-col my-auto gap-0',
                    tabSingleImageFullColumn && 'overflow-hidden md:self-stretch',
                  )}
                >
                  {tabSingleImageFullColumn ? (
                    <div className="relative min-h-[410px] md:h-full w-full overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.12)]">
                      <Image
                        alt={
                          (hasTabImage1 ? current.imageAlt : current.image2Alt) ||
                          current.heading ||
                          'Craft Home Remodel project'
                        }
                        className="object-cover"
                        fill
                        sizes="(max-width: 1024px) 100vw, 50vw"
                        src={(current.imageUrl ?? current.image2Url)!}
                      />
                    </div>
                  ) : (
                    <>
                      {current.imageUrl ? (
                        <div className="relative h-[410px] w-full overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.12)] md:h-[410px]">
                          <Image
                            alt={current.imageAlt || current.heading || 'Craft Home Remodel project'}
                            className="object-cover"
                            fill
                            sizes="(max-width: 1024px) 100vw, 50vw"
                            src={current.imageUrl}
                          />
                        </div>
                      ) : null}
                      {current.image2Url ? (
                        <div className="relative h-[410px] w-full overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.12)] md:h-[410px]">
                          <Image
                            alt={current.image2Alt || current.heading || 'Craft Home Remodel project'}
                            className="object-cover"
                            fill
                            sizes="(max-width: 1024px) 100vw, 50vw"
                            src={current.image2Url}
                          />
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          {/* end content */}
        </div>
      </div>
    </section>
  )
}
